import { APP_RELEASE_ID, APP_VERSION } from "../config/appRelease";
import { planReleaseExperience } from "../experience/ExperiencePlanner";
import { trackEducationEvent } from "../shared/analytics/AnalyticsSink";
import { createPersistenceAdapter } from "../shared/persistence/PersistenceAdapter";
import { resolveBriefScript } from "./briefScriptResolver";
import {
  getFeaturesForRelease,
  groupFeaturesByBundle,
} from "./featureRegistry";
import { detectReleaseUpdate } from "./versionDetector";
import { logReleaseManagerDecision } from "../shared/pepBootDebug";

export function createReleaseManager(userId) {
  const persistence = createPersistenceAdapter(userId);

  function getState() {
    return persistence.load();
  }

  function evaluate({ isGuest = false, context = "auto" } = {}) {
    const state = getState();
    const detection = detectReleaseUpdate(state);
    const plan = planReleaseExperience({
      detection,
      state,
      isGuest,
      context,
    });

    const presentationAction = getPresentationAction(plan);

    logReleaseManagerDecision({
      presentationAction,
      presentationType: presentationAction,
      plan,
    });

    return {
      state,
      detection,
      plan,
      briefScript: resolveBriefScript(
        detection.releaseMeta,
        detection.currentReleaseId,
      ),
    };
  }

  function markBriefShown(releaseId) {
    persistence.updateRelease(releaseId, {
      status: "in_progress",
      briefShownAt: new Date().toISOString(),
    });
    trackEducationEvent("release_brief_shown", { releaseId });
  }

  function handleBriefChoice(choice) {
    const state = getState();
    const releaseId = APP_RELEASE_ID;
    const now = new Date().toISOString();

    if (choice === "show_me") {
      const next = persistence.updateRelease(releaseId, {
        status: "in_progress",
        chosenExperience: "visual",
        briefCompleted: true,
        briefCompletedAt: now,
      });
      trackEducationEvent("release_brief_choice", { releaseId, choice });
      return { action: "start_tour_placeholder", state: next };
    }

    if (choice === "continue") {
      const next = persistence.updateRelease(releaseId, {
        status: "in_progress",
        chosenExperience: "visual",
        briefCompleted: true,
        briefCompletedAt: now,
        tourRequired: true,
      });
      trackEducationEvent("release_brief_choice", { releaseId, choice });
      return { action: "start_required_tour", state: next };
    }

    if (choice === "just_tell_me") {
      const next = persistence.update((current) => ({
        ...upsertReleaseCompletion(current, releaseId, {
          status: "completed",
          chosenExperience: "reader",
          briefCompleted: true,
          whatsNewOpened: true,
          completedAt: now,
        }),
        lastSeenReleaseId: releaseId,
        lastSeenVersion: APP_VERSION,
        settings: {
          ...current.settings,
          learningStyle: "reader",
          learningStyleSource: "explicit",
        },
      }));
      trackEducationEvent("release_brief_choice", { releaseId, choice });
      return { action: "open_whats_new", state: next };
    }

    if (choice === "skip") {
      const next = persistence.update((current) => ({
        ...upsertReleaseCompletion(current, releaseId, {
          status: "skipped",
          chosenExperience: "skip",
          briefDismissed: true,
          skippedAt: now,
        }),
        lastSeenReleaseId: releaseId,
        lastSeenVersion: APP_VERSION,
      }));
      trackEducationEvent("release_brief_choice", { releaseId, choice });
      return { action: "dismiss", state: next };
    }

    return { action: "none", state };
  }

  function completeTourPlaceholder() {
    const releaseId = APP_RELEASE_ID;
    const now = new Date().toISOString();

    const next = persistence.update((current) => ({
      ...upsertReleaseCompletion(current, releaseId, {
        status: "completed",
        chosenExperience: "visual",
        completedAt: now,
      }),
      lastSeenReleaseId: releaseId,
      lastSeenVersion: APP_VERSION,
      settings: {
        ...current.settings,
        learningStyle: "visual",
        learningStyleSource: "explicit",
      },
    }));

    trackEducationEvent("release_tour_completed_placeholder", { releaseId });
    return next;
  }

  function openWhatsNewManual(releaseId = APP_RELEASE_ID) {
    const next = persistence.updateRelease(releaseId, {
      whatsNewOpened: true,
    });
    trackEducationEvent("whats_new_opened", { releaseId, manual: true });
    return next;
  }

  function dismissWhatsNewChip() {
    const releaseId = APP_RELEASE_ID;
    return persistence.updateRelease(releaseId, {
      whatsNewChipDismissed: true,
    });
  }

  function getWhatsNewPayload(releaseId = APP_RELEASE_ID) {
    const manifest = detectReleaseUpdate(getState()).manifest;
    const meta = manifest?.meta;
    const features = getFeaturesForRelease(releaseId).filter(
      (feature) => feature.whatsNew?.include !== false && feature.severity !== "silent",
    );

    return {
      releaseId,
      meta,
      bundles: groupFeaturesByBundle(releaseId, features),
    };
  }

  function updateSettings(partialSettings) {
    return persistence.updateSettings(partialSettings);
  }

  function replayLatestRelease() {
    trackEducationEvent("release_replay_requested", { releaseId: APP_RELEASE_ID });
    return evaluate({ context: "manual" });
  }

  function getPresentationAction(plan) {
    if (plan.showRequiredTour) {
      return "required_tour";
    }
    if (plan.showBrief) {
      return "brief";
    }
    if (plan.showWhatsNewChip) {
      return "whats_new_chip";
    }
    return "none";
  }

  return {
    evaluate,
    getState,
    markBriefShown,
    handleBriefChoice,
    completeTourPlaceholder,
    openWhatsNewManual,
    dismissWhatsNewChip,
    getWhatsNewPayload,
    updateSettings,
    replayLatestRelease,
    getPresentationAction,
    persistence,
  };
}

function upsertReleaseCompletion(state, releaseId, patch) {
  const existing = state.releases[releaseId] || { status: "not_started" };
  return {
    ...state,
    releases: {
      ...state.releases,
      [releaseId]: {
        ...existing,
        ...patch,
        updatedAt: new Date().toISOString(),
      },
    },
  };
}
